import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Box, InputAdornment, MenuItem, type SelectChangeEvent } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import type { BlockKindFilterOption } from '@/blocks/domain/helpers/buildKindFilterOptions';
import type { BlockKind, BlockKindFilterValue } from '@/blocks/domain/types';
import { BLOCK_KIND_LABELS } from '@/blocks/domain/types';
import { RwField, RwSelect, RwToggle } from '@/common/presentation/components/rw';
import { useBlockLibraryUrlFilters } from '@/blocks/presentation/hooks/useBlockLibraryUrlFilters';

const SEARCH_DEBOUNCE_MS = 200;
const KIND_FILTER_ALL = 'all' as const;

export interface BlockLibraryFiltersProps {
  /** The kinds present in the current results (see `buildKindFilterOptions`); "All kinds" is added here. */
  kindOptions: ReadonlyArray<BlockKindFilterOption>;
}

const kindLabel = (options: ReadonlyArray<BlockKindFilterOption>, value: string): string =>
  options.find((option) => option.value === value)?.label ?? BLOCK_KIND_LABELS[value as BlockKind] ?? value;

/**
 * The Block Library's one filter row (Figma `bl.library.default`, node `6:3`): search → "All
 * kinds" → "Show archived". All three read and write `useBlockLibraryUrlFilters` DIRECTLY — no
 * `useState` mirror of the committed filter values and no sync effect re-deriving them from the
 * URL. That mirror-plus-effect shape is exactly what made "Show archived" revert itself in the
 * designer prototype (a resync effect fired on every render the URL changed and stomped local
 * state back to stale). Reading nuqs's own state as the single source of truth removes the
 * failure mode entirely — there is nothing here for that class of bug to happen to.
 *
 * The one local state is a debounce buffer for the search box's keystrokes (`draftSearch`), which
 * exists purely to avoid a URL write (and therefore a refetch) on every keypress; it is written
 * FROM the URL on external navigation (back/forward, a shared link) and never the reverse until
 * the debounce timer commits, so it cannot fight a resolved filter value the way the removed
 * mirror did.
 */
export function BlockLibraryFilters({ kindOptions }: BlockLibraryFiltersProps) {
  const [filters, setFilters] = useBlockLibraryUrlFilters();
  const [draftSearch, setDraftSearch] = useState(filters.q);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDraftSearch(filters.q);
  }, [filters.q]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    setDraftSearch(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void setFilters({ q: next });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleKindChange = (event: SelectChangeEvent<unknown>) => {
    void setFilters({ kind: event.target.value as BlockKindFilterValue | typeof KIND_FILTER_ALL });
  };

  // The legacy `?status=Archived` URL lists archived blocks only: it is still "Show archived", so the switch
  // reads ON for it (turning it off returns to the active library).
  const showArchived = filters.status === 'all' || filters.status === 'Archived';

  const handleShowArchivedChange = (checked: boolean) => {
    void setFilters({ status: checked ? 'all' : 'all-active' });
  };

  return (
    <Box
      role="search"
      aria-label="Block library filters"
      sx={{ display: 'flex', alignItems: 'center', gap: 'var(--size-16)', mb: 'var(--size-24)', flexWrap: 'wrap' }}
    >
      {/* No visible label (Figma `bl.library.default`): the magnifier and the placeholder say what it is,
          and the accessible name rides on the input itself. */}
      <RwField
        type="search"
        placeholder="Search blocks"
        value={draftSearch}
        onChange={handleSearchChange}
        size="small"
        sx={{ minWidth: 0, flex: '1 1 var(--size-256)', maxWidth: { xs: '100%', sm: 'var(--size-400)' } }}
        slotProps={{
          htmlInput: { 'aria-label': 'Search blocks' },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" aria-hidden />
              </InputAdornment>
            )
          }
        }}
      />

      <RwSelect
        value={filters.kind}
        onChange={handleKindChange}
        size="small"
        inputProps={{ 'aria-label': 'Kind' }}
        renderValue={(value) => (value === KIND_FILTER_ALL ? 'All kinds' : kindLabel(kindOptions, String(value)))}
        // No `--size-160` token: 10rem is the 160px floor the kind select shrinks to on a phone.
        sx={{
          minWidth: 0,
          width: { xs: '100%', sm: 'var(--size-256)' },
          flex: { xs: '1 1 10rem', sm: '0 0 var(--size-256)' },
          maxWidth: '100%'
        }}
      >
        <MenuItem value={KIND_FILTER_ALL}>All kinds</MenuItem>
        {kindOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </RwSelect>

      {/* SHOW ARCHIVED — a Toggle, not a Content switcher (design ruling 2026-09-07): archived is
          not a fourth view of the list, it is extra rows added to the one view, so a reversible
          binary is the honest control. Label sits LEFT of the switch — the dense in-a-row layout
          the DS reserves for exactly this. */}
      <RwToggle
        checked={showArchived}
        onChange={handleShowArchivedChange}
        label="Show archived"
        labelPosition="left"
        name="show-archived"
      />
    </Box>
  );
}
